import { AnimateLayoutChanges, defaultAnimateLayoutChanges, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Tooltip } from '@systemeio/ui'
import FunnelStepActions from 'modules/funnels/funnel/components/funnel-actions'
import TestIconWithoutBg from 'modules/funnels/funnel/icons/test-withot-bg-icon'
import { FunnelStepInterface } from 'modules/funnels/funnel/types/funnel-step-interface'
import { getFunnelStepTypeIcon } from 'modules/funnels/funnel/utils/get-funnel-step-type-icon'
import React from 'react'
import { FunnelStepTypeTitlesEnum } from 'shared/enums/funnel-step-type-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { CheckIcon, WarningIcon } from '@systemeio/ui/icons'

interface DraggableFunnelStepProps {
  funnelStep: FunnelStepInterface
  selectedFunnelStep: FunnelStepInterface | null
  selectFunnelStep: (funnelId: FunnelStepInterface) => void
}

const animateLayoutChanges: AnimateLayoutChanges = args =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true })

function DraggableFunnelStep({
  funnelStep,
  selectedFunnelStep,
  selectFunnelStep,
}: DraggableFunnelStepProps) {
  const { t } = useLocoTranslation()
  const { attributes, isDragging, listeners, setNodeRef, transition, transform } = useSortable({
    id: funnelStep.id,
    animateLayoutChanges,
  })

  const style: React.CSSProperties = {
    transition,
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : undefined,
  }

  const isGoalFunnelStepOfAnySplitTest = funnelStep.goalFunnelStepOfAnySplitTest
  const isNotGoalInSplitTest =
    !funnelStep.goalFunnelStepOfAnySplitTest &&
    (funnelStep.inRunningSplitTest || funnelStep.currentSplitTest?.id)

  return (
    <li
      className={`lg:hover:bg-brand-primary/[0.06] border-[rgb(var(--text-primary))]/20 relative flex cursor-pointer justify-between border-b-[1px] px-[15px] py-[18px] last:border-b-transparent ${
        selectedFunnelStep?.id === funnelStep.id ? 'bg-brand-primary/[0.06]' : ''
      }`}
      ref={setNodeRef}
      style={{ ...style }}
      {...attributes}
      {...listeners}
      onClick={() => selectFunnelStep(funnelStep)}
    >
      <div className="flex items-start gap-3">
        <div className="mt-1">{getFunnelStepTypeIcon(funnelStep.type)}</div>
        <div className="flex max-w-[200px] flex-col gap-1">
          <Tooltip
            mode="overflow"
            label={funnelStep.name}
            wrapperClassName="flex w-full h-full"
            className="font-inter text-[15px] font-semibold leading-5 "
            popoverClassName={'max-w-[250px]'}
            key={funnelStep.name}
          >
            {funnelStep.name}
          </Tooltip>
          <div className="flex items-center gap-1">
            {!funnelStep.page && (
              <WarningIcon className="h-[20px] w-[16px] shrink-0 fill-[rgb(var(--fill-primary))]" />
            )}
            <span className="text-text-tertiary truncate text-sm font-normal leading-4">
              {t(FunnelStepTypeTitlesEnum[funnelStep.type])}
            </span>
          </div>
        </div>
      </div>
      {isGoalFunnelStepOfAnySplitTest && (
        <div className="absolute left-0.5 top-0.5">
          <Tooltip
            label={t('dashboard.funnel.split_tests.goal_page_tooltip')}
            popoverClassName={'min-w-max'}
            mode="hover"
          >
            <CheckIcon className="stroke-[rgb(var(--fill-primary))] h-4 w-4" />
          </Tooltip>
        </div>
      )}
      {isNotGoalInSplitTest && (
        <div className="absolute left-0.5 top-0.5">
          <Tooltip
            label={
              funnelStep.inRunningSplitTest
                ? t('dashboard.funnel.split_tests.running_tooltip')
                : t('dashboard.funnel.split_tests.selected_tooltip')
            }
            popoverClassName={'min-w-max'}
            mode="hover"
          >
            <TestIconWithoutBg className="hover:fill-bg-brand-primary/[0.06]" />
          </Tooltip>
        </div>
      )}
      <FunnelStepActions funnelStep={funnelStep} selectFunnelStep={selectFunnelStep} />
    </li>
  )
}

export default DraggableFunnelStep
